import React from "react";
import { useQuery } from "react-query";

const TASK_STATE_CREATED = "CREATED";
const TASK_STATE_STARTED = "STARTED";
const TASK_STATE_SUCCESS = "SUCCESS";
const TASK_STATE_ERROR = "ERROR";

export default function useTaskStatusQuery({
	taskId,
	queryFn,
	options = {},
	toast,
	defaultTiming = 250,
	maxTiming = 5000,
	onTaskSuccess,
	onTaskError
}) {
	const lastRefetchRef = React.useRef(null);
	const lastTimingRef = React.useRef(defaultTiming);

	const taskStatusQuery = useQuery(
		["task-status", taskId],
		() => queryFn(taskId),
		{
			enabled: !!taskId,
			onSuccess: data => {
				if (!data) return;

				const state = data?.state;

				if (state === TASK_STATE_SUCCESS) {
					lastTimingRef.current = defaultTiming;

					if (options.successMessage) {
						toast.success(options.successMessage);
					}

					onTaskSuccess?.(data);
					return;
				}

				if (state === TASK_STATE_ERROR) {
					lastTimingRef.current = defaultTiming;

					if (options.showErrorToast !== false) {
						if (options.errorMessage) {
							toast.error(options.errorMessage);
						} else if (data?.error?.message) {
							toast.error(data.error.message);
						}
					}

					onTaskError?.(data?.error);
					return;
				}
			}
		}
	);

	React.useEffect(() => {
		if (!taskId) {
			return;
		}

		// If the query is currently loading, wait
		if (taskStatusQuery.isLoading && !taskStatusQuery.data) {
			return;
		}

		// If we don't have any data yet, we immediately trigger the refetch
		if (!taskStatusQuery.data && !taskStatusQuery.isLoading) {
			taskStatusQuery.refetch();
			return;
		}

		const state = taskStatusQuery.data?.state;

		// If we are in a final state, we stop
		if (state === TASK_STATE_SUCCESS || state === TASK_STATE_ERROR) {
			lastTimingRef.current = defaultTiming;
			return;
		}

		const shouldRefetch =
			state === TASK_STATE_CREATED || state === TASK_STATE_STARTED;

		if (!shouldRefetch) {
			return;
		}

		if (lastRefetchRef.current) {
			return;
		}

		lastRefetchRef.current = setTimeout(() => {
			taskStatusQuery.refetch().finally(() => {
				lastRefetchRef.current = null;
			});
		}, lastTimingRef.current);

		lastTimingRef.current = Math.min(lastTimingRef.current * 2, maxTiming);

		return () => {
			if (lastRefetchRef.current) {
				clearTimeout(lastRefetchRef.current);
				lastRefetchRef.current = null;
			}
		};
	}, [taskStatusQuery, taskId, defaultTiming, maxTiming]);

	return {
		taskStatusQuery,
		lastRefetchRef,
		lastTimingRef,
		resetTiming: () => {
			lastTimingRef.current = defaultTiming;
		}
	};
}
